<template>
  <div class="country-code-select" v-on-clickaway="close">
    <button type="button" class="country-select-toggle" @click="toggle">
      <span>Add country...</span>
      <span class="arrow-down" />
    </button>

    <div v-show="open" class="country-dropdown">
      <div class="country-search">
        <input
          ref="searchInput"
          v-model="search"
          placeholder="Search countries..."
          class="form-control form-control-sm"
          type="text"
        />
      </div>

      <ul class="country-list">
        <template v-for="group in filteredGroups">
          <!-- Group header -->
          <li :key="'header-' + group.label" class="country-group-header">
            <span class="group-label">{{ group.label }}</span>
            <span class="ml-auto d-flex align-items-center">
              <button
                v-if="group.unselectedVisible.length > 0"
                type="button"
                class="btn btn-link btn-sm p-0 add-all-btn"
                @click.stop="addAll(group)"
              >
                + Add all ({{ group.unselectedVisible.length }})
              </button>
              <span v-if="group.unselectedVisible.length > 0 && group.selectedVisible.length > 0" class="mx-1 text-muted">·</span>
              <button
                v-if="group.selectedVisible.length > 0"
                type="button"
                class="btn btn-link btn-sm p-0 remove-all-btn"
                @click.stop="removeAll(group)"
              >
                − Remove all ({{ group.selectedVisible.length }})
              </button>
            </span>
          </li>

          <!-- Country items -->
          <li
            v-for="country in group.visibleItems"
            :key="country.value"
            class="country-item"
            :class="{ 'is-selected': country.selected }"
            @click="toggleCountry(country)"
          >
            <span class="check-col">
              <i v-if="country.selected" class="fa fa-check" />
            </span>
            <img
              :src="'https://flagcdn.com/16x12/' + country.value.toLowerCase() + '.png'"
              :srcset="'https://flagcdn.com/32x24/' + country.value.toLowerCase() + '.png 2x'"
              width="16"
              height="12"
              :alt="country.value"
              class="country-flag mr-2"
              @error="function(e) { e.target.style.display = 'none'; }"
            />
            {{ country.text }}
            <span class="country-code ml-auto">({{ country.value }})</span>
          </li>
        </template>

        <li v-if="hasNoResults" class="country-item disabled">
          <em class="text-muted">No countries match "{{ search }}"</em>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';

export default {
  name: 'CountryCodeSelect',
  mixins: [clickaway],

  props: {
    // flat grouped list from getGroupedCountryCodeList — items with disabled:true are headers
    groupedOptions: {
      type: Array,
      default: function() { return []; }
    },
    // currently selected values
    excludeValues: {
      type: Array,
      default: function() { return []; }
    }
  },

  data: function() {
    return {
      open: false,
      search: ''
    };
  },

  computed: {
    // Parse flat grouped list into [{label, items:[]}] structure
    groups: function() {
      var groups = [];
      var current = null;
      (this.groupedOptions || []).forEach(function(opt) {
        if (opt.disabled) {
          current = { label: opt.text.replace(/──\s*/g, '').replace(/\s*──/g, '').trim(), items: [] };
          groups.push(current);
        } else if (current) {
          current.items.push(opt);
        }
      });
      return groups;
    },

    filteredGroups: function() {
      var self = this;
      var q = self.search.toLowerCase().trim();
      var result = [];

      self.groups.forEach(function(group) {
        var visibleItems = group.items
          .filter(function(c) {
            if (!q) return true;
            return c.text.toLowerCase().indexOf(q) !== -1 || c.value.toLowerCase().indexOf(q) !== -1;
          })
          .map(function(c) {
            return { value: c.value, text: c.text, selected: self.excludeValues.indexOf(c.value) !== -1 };
          });

        if (visibleItems.length === 0) return;

        var selectedVisible = visibleItems.filter(function(c) { return c.selected; });
        var unselectedVisible = visibleItems.filter(function(c) { return !c.selected; });

        result.push({
          label: group.label,
          items: group.items,
          visibleItems: visibleItems,
          selectedVisible: selectedVisible,
          unselectedVisible: unselectedVisible
        });
      });

      return result;
    },

    hasNoResults: function() {
      return this.search.length > 0 && this.filteredGroups.length === 0;
    }
  },

  methods: {
    toggle: function() {
      this.open = !this.open;
      if (this.open) {
        var self = this;
        setTimeout(function() {
          if (self.$refs.searchInput) self.$refs.searchInput.focus();
        }, 60);
      }
    },

    close: function() {
      this.open = false;
      this.search = '';
    },

    toggleCountry: function(country) {
      if (country.selected) {
        this.$emit('remove-many', [country.value]);
      } else {
        this.$emit('add', country.value);
      }
    },

    addAll: function(group) {
      var values = group.unselectedVisible.map(function(c) { return c.value; });
      this.$emit('add-many', values);
    },

    removeAll: function(group) {
      var values = group.selectedVisible.map(function(c) { return c.value; });
      this.$emit('remove-many', values);
    }
  }
};
</script>

<style scoped>
.country-code-select {
  position: relative;
  display: inline-block;
  width: 100%;
}

.country-select-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #495057;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  cursor: pointer;
  height: calc(1.5em + 0.5rem + 2px);
}
.country-select-toggle:focus {
  border-color: #7489ac;
  outline: 0;
  box-shadow: 0 0 0 1px rgb(159, 172, 196);
}

.arrow-down {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 0.4em;
  vertical-align: middle;
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-bottom: 0;
  border-left: 0.3em solid transparent;
  flex-shrink: 0;
}

.country-dropdown {
  position: absolute;
  z-index: 1050;
  width: 320px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  margin-top: 2px;
}

.country-search {
  padding: 6px 8px;
  border-bottom: 1px solid #efefef;
}

.country-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 300px;
  overflow-y: auto;
}

.country-group-header {
  display: flex;
  align-items: center;
  padding: 5px 10px 3px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  position: sticky;
  top: 0;
  z-index: 1;
}

.group-label {
  flex-shrink: 0;
}

.add-all-btn {
  font-size: 0.72rem !important;
  color: #007bff !important;
  text-decoration: none !important;
  white-space: nowrap;
}
.add-all-btn:hover {
  text-decoration: underline !important;
}
.remove-all-btn {
  font-size: 0.72rem !important;
  color: #dc3545 !important;
  text-decoration: none !important;
  white-space: nowrap;
}
.remove-all-btn:hover {
  text-decoration: underline !important;
}

.country-item {
  display: flex;
  align-items: center;
  padding: 4px 8px 4px 4px;
  font-size: 0.8rem;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  user-select: none;
}
.country-item:hover {
  background: #e8f0fe;
}
.country-item.is-selected {
  background: #eef4ff;
}
.country-item.is-selected:hover {
  background: #dce8fd;
}
.country-item.disabled {
  cursor: default;
  color: #aaa;
  padding: 6px 12px;
}

.check-col {
  width: 18px;
  flex-shrink: 0;
  text-align: center;
  font-size: 0.65rem;
  color: #007bff;
}

.country-flag {
  flex-shrink: 0;
  border: 1px solid #ddd;
}

.country-code {
  font-size: 0.72rem;
  color: #999;
  flex-shrink: 0;
}
</style>
